import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { X, FileCheck } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

interface CAROClauseIChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (responses: Record<string, string>) => void;
  initialResponses?: Record<string, string>;
}

interface Question {
  id: string;
  text: string;
  dependsOn?: string;
  showIf?: string;
}

const questions: Question[] = [
  {
    id: "q1",
    text: "Is the company maintaining proper records showing full particulars, including quantitative details and situation of PPE?"
  },
  {
    id: "q2", 
    text: "Is the company maintaining proper records showing full particulars of intangible assets?"
  },
  {
    id: "q3",
    text: "Has the management physically verified PPE at reasonable intervals?"
  },
  {
    id: "q4",
    text: "Were material discrepancies noticed during physical verification?",
    dependsOn: "q3",
    showIf: "yes"
  },
  {
    id: "q5",
    text: "If discrepancies were noticed, were they properly dealt with in the books of account?",
    dependsOn: "q4",
    showIf: "yes"
  },
  {
    id: "q6",
    text: "Are all assets physically verified during the year?",
    dependsOn: "q3",
    showIf: "yes"
  },
  {
    id: "q7",
    text: "Are the assets physically verified at a point in time?",
    dependsOn: "q3",
    showIf: "yes"
  },
  {
    id: "q8",
    text: "Has the company disclosed immovable properties in its financial statements?"
  },
  {
    id: "q9",
    text: "Are the title deeds of all immovable properties disclosed in the financial statements held in the company's name?",
    dependsOn: "q8",
    showIf: "yes"
  },
  {
    id: "q10",
    text: "Does the company have leased immovable properties?",
    dependsOn: "q8",
    showIf: "yes"
  },
  {
    id: "q11",
    text: "Are lease agreements duly executed in favour of the company?",
    dependsOn: "q10",
    showIf: "yes"
  },
  {
    id: "q12",
    text: "Are any title deeds under legal dispute?",
    dependsOn: "q8",
    showIf: "yes"
  },
  {
    id: "q13",
    text: "Has the company revalued its PPE or intangible assets during the year?"
  },
  {
    id: "q14",
    text: "If revalued, was the revaluation done by a Registered Valuer?",
    dependsOn: "q13",
    showIf: "yes"
  },
  {
    id: "q15",
    text: "Did the revaluation result in a change of 10% or more in the aggregate net carrying value of each class of PPE or intangible assets?",
    dependsOn: "q13",
    showIf: "yes"
  },
  {
    id: "q16",
    text: "Are there any proceedings initiated or pending against the company for holding benami property under the Benami Transactions (Prohibition) Act?"
  },
  {
    id: "q17",
    text: "If yes, has the company appropriately disclosed the details in its financial statements?",
    dependsOn: "q16",
    showIf: "yes"
  },
  {
    id: "q18",
    text: "Have any proceedings been disposed off during the financial year/subsequent to the balance sheet date?",
    dependsOn: "q16",
    showIf: "yes"
  },
  {
    id: "q19",
    text: "Have any proceedings been initiated subsequent to the balance sheet date?",
    dependsOn: "q16",
    showIf: "yes"
  }
];

export default function CAROClauseIChecklistModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialResponses = {}
}: CAROClauseIChecklistModalProps) {
  const { isArchivalMode } = useArchivalMode();
  const [responses, setResponses] = useState<Record<string, string>>(initialResponses);

  // Update responses when initialResponses changes (when modal opens with new data)
  React.useEffect(() => {
    if (isOpen && initialResponses) {
      setResponses(initialResponses);
    }
  }, [isOpen, initialResponses]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const shouldShowQuestion = (question: Question): boolean => {
    if (!question.dependsOn) return true;
    
    const dependentResponse = responses[question.dependsOn];
    return dependentResponse === question.showIf;
  };

  const handleSave = () => {
    onSave(responses);
    onClose();
  };

  const handleClose = () => {
    setResponses({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-4xl max-h-[90vh] border-white/10 bg-black/80 text-white shadow-2xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileCheck className="h-5 w-5 text-[#4da3ff]" />
            <CardTitle className="text-lg">
              CARO Clause (i) - Property, Plant & Equipment and Intangible Assets
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {questions.map((question, index) => {
              const isVisible = shouldShowQuestion(question);
              
              if (!isVisible) return null;

              return (
                <div key={question.id} className="space-y-3">
                  <p className="text-sm text-white/90">
                    {question.text}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="yes"
                        checked={responses[question.id] === "yes"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="no"
                        checked={responses[question.id] === "no"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">No</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>

        <div className="flex justify-end gap-3 p-6 border-t border-white/10">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white/20 text-white/80 hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
            disabled={isArchivalMode}
          >
            Save Responses
          </Button>
        </div>
      </Card>
    </div>
  );
}
